// Vue
import { ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { CalculationTypesType } from "../types/CalculationTypesType";
import type { CalculationType } from "@/graphql";

// GraphQL
import { GetCalculationTypeQuery } from "@/graphql";

interface CalculationTypesProvider {
  getCalculationTypes: () => CalculationTypesType;
  setCalculationTypes: (newTypes: CalculationTypesType) => void;
  mapData(data: GetCalculationTypeQuery): void;
  getSelectedCalculationType: (selectedCalculationTypeId: number) => CalculationTypesType[0] | undefined;
  getThresholds(
    calculationType: CalculationType,
    buildingTypeId: number,
    totalArea: number,
    noFallback: boolean,
    customThreshold?: number
  ): {
    threshold?: number;
    installationalThreshold?: number;
  };
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useCalculationTypes = defineStore("CalculationTypesProvider", (): CalculationTypesProvider => {
  const calculationTypes = ref<CalculationTypesType>([]);

  const getSelectedCalculationType = (selectedCalculationTypeId: number): CalculationTypesType[0] | undefined => {
    return calculationTypes.value.find((x) => x.id === selectedCalculationTypeId);
  };

  function getCalculationTypes() {
    return calculationTypes.value;
  }

  function setCalculationTypes(newTypes: CalculationTypesType) {
    calculationTypes.value = newTypes;
  }

  function mapData(data: GetCalculationTypeQuery) {
    const { calculationType } = data;

    // Map all models
    if (!calculationType) return;
    setCalculationTypes(calculationType);
    // Mapped data means the load is over, wherever the data came from — the fetcher component in
    // the app, or a direct seed in stories. `loading` starts true, so consumers that gate on it
    // (the start dialog disables its selects while a country's types load) need this flip here.
    setLoading(false);
  }

  function getThresholds(
    calculationType: CalculationType,
    buildingTypeId: number,
    totalArea: number,
    noFallback: boolean
  ): {
    threshold?: number;
    installationalThreshold?: number;
  } {
    const fallbackThreshold = calculationType.threshold;
    const thresholdByBuildingType = calculationType.thresholdByBuildingType;

    let match = thresholdByBuildingType.find((x) => x.buildingTypeId === buildingTypeId && totalArea <= x.maxTotalArea);

    // If no threshold is found for the specific building type and total area, look for a row where maxTotalArea is falsey
    if (match === undefined) {
      match = thresholdByBuildingType.find((x) => x.buildingTypeId === buildingTypeId && !x.maxTotalArea);
    }
    const thresholdValue = noFallback ? match?.threshold : match?.threshold ?? fallbackThreshold;
    return {
      threshold: thresholdValue,
      installationalThreshold: match?.installationThreshold,
    };
  }

  const loading = ref<boolean>(true);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  return {
    getCalculationTypes,
    getSelectedCalculationType,
    setCalculationTypes,
    mapData,
    getThresholds,
    setLoading,
    getLoading,
  };
});

export default useCalculationTypes;
